import React from 'react';
import * as Select from '@radix-ui/react-select';
import { XIcon, CheckIcon, CaretDownIcon } from '@phosphor-icons/react';

import classNames from '@utils/classnames';
import { controlClassName, popoverItemClassName, popoverPanelClassName } from '../form/control';

interface ISelectItemProps {
  children: React.ReactNode;
  value: string;
  selected: boolean | null;
  className?: string;
}

const SelectItem = React.forwardRef<HTMLDivElement | null, ISelectItemProps>((props, forwardedRef) => {
  const { children, selected, className, ...otherProps } = props;

  return (
    <Select.Item className={classNames(popoverItemClassName, className)} {...otherProps} ref={forwardedRef}>
      <Select.ItemText>{children}</Select.ItemText>
      {selected && (
        <Select.ItemIndicator className="absolute inset-y-0 right-0 flex items-center pr-2.5 text-brand-text">
          <CheckIcon className="h-4 w-4" weight="bold" aria-hidden="true" />
        </Select.ItemIndicator>
      )}
    </Select.Item>
  );
});

export interface ISelectProps<V extends Record<string, any>> {
  placeholder?: string;
  items: V[];
  selectedItem: V | null;
  onSelect: (value: V | null) => void;
  keyName: string;
  display: (value: V) => string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  onBlur?: () => void;
  isOptional?: boolean;
}

export function CustomSelect<V extends Record<string, any>>(props: ISelectProps<V>) {
  const { placeholder, keyName, items, display, selectedItem, onSelect, isDisabled, isInvalid, onBlur, isOptional } =
    props;
  const [key, setKey] = React.useState(0);

  return (
    <div className="flex w-full gap-1">
      <div className="w-full min-w-0">
        <Select.Root
          value={selectedItem ? selectedItem[keyName] : null}
          onValueChange={(value) => {
            const item = items.find((item) => item[keyName] === value);
            onSelect(item || null);
          }}
          onOpenChange={(open) => {
            if (!open) {
              onBlur?.();
            }
          }}
          disabled={isDisabled}
          required={!isOptional}
          key={key}
        >
          <Select.Trigger
            className={classNames(
              controlClassName({ isInvalid, isDisabled, focusWithin: false }),
              'flex items-center justify-between gap-2 px-2.5 text-left',
              'data-placeholder:text-fg-muted [&>span:first-child]:truncate',
            )}
          >
            <Select.Value placeholder={selectedItem ? display(selectedItem) : (placeholder ?? '')} />
            <Select.Icon className="shrink-0">
              <CaretDownIcon className="h-4 w-4 text-fg-muted" aria-hidden="true" />
            </Select.Icon>
          </Select.Trigger>

          <Select.Portal>
            <Select.Content
              className={classNames(popoverPanelClassName, 'mt-1 max-h-60 w-full')}
              style={{
                minWidth: 'var(--radix-select-trigger-width)',
                maxHeight: 'var(--radix-select-content-available-height)',
              }}
              position="popper"
            >
              <Select.Viewport>
                {items.map((item) => (
                  <SelectItem
                    key={item[keyName]}
                    value={item[keyName]}
                    selected={selectedItem && item[keyName] === selectedItem[keyName]}
                  >
                    {display(item)}
                  </SelectItem>
                ))}
              </Select.Viewport>
            </Select.Content>
          </Select.Portal>
        </Select.Root>
      </div>

      {Boolean(selectedItem && isOptional) && (
        <button
          type="button"
          aria-label="Wis selectie"
          className="flex h-9 w-8 shrink-0 items-center justify-center rounded-md text-fg-muted transition-colors hover:bg-surface-hover hover:text-fg"
          onClick={(evt) => {
            evt.stopPropagation();
            evt.preventDefault();

            onSelect(null);
            setKey((key) => key + 1);
          }}
        >
          <XIcon className="button-icon" />
        </button>
      )}
    </div>
  );
}

export { CustomSelect as Select };
